import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, AlertTriangle, Printer, Lock } from "lucide-react";
import {
  newSession, attachProfile, saveDraft, loadDraft, clearDraft, saveKillSheet,
} from "@/lib/killsheet/sessionStore";
import { listProfiles } from "@/lib/profiles/storage";
import { composeKillSheet } from "@/lib/killsheet/composer";
import { validateSession } from "@/lib/killsheet/validate";
import { isProActive } from "@/lib/featureAccess";
import { getCachedEntitlement } from "@/lib/entitlement";
import SubscribeModal from "@/components/SubscribeModal";

const VARIANT_LABELS = {
  "surface-vertical":   "Surface BOP · Vertical Well",
  "surface-horizontal": "Surface BOP · Horizontal Well",
  "subsea-vertical":    "Subsea BOP · Vertical Well",
  "subsea-horizontal":  "Subsea BOP · Horizontal Well",
};

const STEPS = ["Setup", "Auto-Pop", "Well Snapshot", "Kick Data", "Review", "Export"];

export default function KillSheetWizard() {
  const { variant } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState(() => loadDraft() || newSession(variant));
  const [step, setStep] = useState(0);
  const [composed, setComposed] = useState(null);
  const pro = isProActive(getCachedEntitlement());
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => { saveDraft(session); }, [session]);

  // When the variant in the URL differs from the draft, reset.
  useEffect(() => {
    if (variant && session.variant !== variant) {
      const fresh = newSession(variant);
      setSession(fresh);
      setStep(0);
      setComposed(null);
    }
  }, [variant]);

  const set = (patch) => setSession((s) => ({ ...s, ...patch }));

  const canAdvance = useMemo(() => {
    if (step === 0) {
      return session.refs.well && session.refs.bha && session.refs.casing
          && session.refs.mud && session.refs.pump;
    }
    if (step === 3) {
      const v = validateSession(session);
      return v.ok;
    }
    return true;
  }, [step, session]);

  const compute = () => {
    const r = composeKillSheet(session);
    setComposed(r);
    if (r.ok) setStep(4);
  };

  return (
    <div data-testid="killsheet-wizard" className="max-w-4xl mx-auto px-4 py-6">
      <Link to="/reports" className="inline-flex items-center gap-1 text-[11px] uppercase font-mono tracking-wider text-[var(--rs-text-3)] hover:text-[var(--rs-text-1)] mb-3">
        <ArrowLeft className="w-3 h-3" /> Reports
      </Link>
      <header className="mb-4">
        <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-[var(--rs-text-3)]">
          IWCF Kill Sheet · {variant}
        </div>
        <h1 className="text-[20px] font-semibold tracking-tight text-[var(--rs-text-1)]">
          {VARIANT_LABELS[variant] || "Kill Sheet"}
        </h1>
      </header>

      <Progress step={step} />

      <div className="card-rs p-4 mt-4">
        {step === 0 && <StepSetup session={session} setSession={setSession} />}
        {step === 1 && <StepAutoPop session={session} setSession={setSession} />}
        {step === 2 && <StepWellSnapshot session={session} />}
        {step === 3 && <StepKickData session={session} setSession={setSession} />}
        {step === 4 && <StepReview session={session} composed={composed} />}
        {step === 5 && <StepExport session={session} composed={composed} onSaved={() => { clearDraft(); nav("/reports"); }} />}
      </div>

      <Footer
        step={step}
        canAdvance={canAdvance}
        onPrev={() => setStep((s) => Math.max(0, s - 1))}
        onNext={() => {
          if (step === 3) {
            // Gate the Calculate action itself — Free users can fill in
            // SIDPP/SICP/Pit Gain above, but the actual kill sheet
            // computation (and the Review step that displays it) is
            // Pro-only, so never advance/compute without entitlement.
            if (!pro) { setUpgradeOpen(true); return; }
            compute();
          } else {
            setStep((s) => Math.min(STEPS.length - 1, s + 1));
          }
        }}
        onExport={() => setStep(5)}
        nextLabel={step === 3 ? "Compute Kill →" : "Next →"}
        last={step === STEPS.length - 1}
      />

      <SubscribeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
}

function Progress({ step }) {
  return (
    <ol data-testid="ks-progress" className="flex gap-2 text-[10px] uppercase tracking-wider font-mono">
      {STEPS.map((s, i) => (
        <li key={s} data-testid={`ks-progress-${i}`}
            className={`flex-1 px-2 py-1.5 text-center rounded ${i === step ? "bg-[var(--rs-amber)] text-[#0A0B0E]" : i < step ? "text-[var(--rs-amber)] border border-[var(--rs-border)]" : "text-[var(--rs-text-3)] border border-[var(--rs-border)]"}`}>
          {i + 1}. {s}
        </li>
      ))}
    </ol>
  );
}

function Footer({ step, canAdvance, onPrev, onNext, onExport, nextLabel, last }) {
  return (
    <div className="flex items-center justify-between mt-3">
      <button data-testid="ks-prev" onClick={onPrev} disabled={step === 0}
              className="text-[12px] px-3 py-2 rounded border border-[var(--rs-border)] text-[var(--rs-text-2)] disabled:opacity-30"
              style={{ minHeight: 36 }}>← Back</button>
      {step === 4 ? (
        <button data-testid="ks-export" onClick={onExport}
                className="text-[12px] px-4 py-2 rounded font-medium"
                style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 36 }}>
          Continue to Export →
        </button>
      ) : !last ? (
        <button data-testid="ks-next" onClick={onNext} disabled={!canAdvance}
                className="text-[12px] px-4 py-2 rounded font-medium disabled:opacity-30"
                style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 36 }}>
          {nextLabel}
        </button>
      ) : null}
    </div>
  );
}

function StepSetup({ session, setSession }) {
  const wells   = listProfiles("well");
  const bhas    = listProfiles("bha");
  const csgs    = listProfiles("casing");
  const muds    = listProfiles("mud");
  const pumps   = listProfiles("pump");
  const pick = (kind, id) => {
    if (!id) return;
    try {
      const next = attachProfile({ ...session }, kind, id);
      setSession(next);
    } catch (e) { alert(e.message); }
  };
  return (
    <div data-testid="ks-step-setup" className="flex flex-col gap-3">
      <h2 className="text-[14px] font-semibold text-[var(--rs-text-1)]">Pick your profiles</h2>
      <p className="text-[11px] text-[var(--rs-text-3)]">
        Pick the four profile types this kill sheet references. Each pick locks
        a snapshot — later edits to the profile do not change this kill sheet.
      </p>
      <ProfilePicker kind="well"   label="Well Profile"        rows={wells} current={session.refs.well}   onPick={(id) => pick("well", id)}   />
      <ProfilePicker kind="bha"    label="BHA Profile"         rows={bhas}  current={session.refs.bha}    onPick={(id) => pick("bha", id)}    />
      <ProfilePicker kind="casing" label="Casing Profile"      rows={csgs}  current={session.refs.casing} onPick={(id) => pick("casing", id)} />
      <ProfilePicker kind="mud"    label="Mud Profile"         rows={muds}  current={session.refs.mud}    onPick={(id) => pick("mud", id)}    />
      <ProfilePicker kind="pump"   label="Pump Profile"        rows={pumps} current={session.refs.pump}   onPick={(id) => pick("pump", id)}   />
      <div className="grid grid-cols-2 gap-3 mt-2">
        <label className="text-[11px]">
          <span className="text-[var(--rs-text-3)] block mb-1">Units</span>
          <select data-testid="ks-setup-units" value={session.units} onChange={(e) => setSession({ ...session, units: e.target.value })} className="input-rs">
            <option value="IMP">Imperial</option>
            <option value="SI">SI</option>
          </select>
        </label>
        <label className="text-[11px]">
          <span className="text-[var(--rs-text-3)] block mb-1">Date</span>
          <input data-testid="ks-setup-date" type="date" value={session.date} onChange={(e) => setSession({ ...session, date: e.target.value })} className="input-rs" />
        </label>
      </div>
    </div>
  );
}

function ProfilePicker({ kind, label, rows, current, onPick }) {
  return (
    <label className="text-[11px]">
      <span className="text-[var(--rs-text-3)] block mb-1">{label}</span>
      <div className="flex gap-2">
        <select data-testid={`ks-setup-${kind}`} value={current?.id || ""} onChange={(e) => onPick(e.target.value)} className="input-rs flex-1">
          <option value="">Select…</option>
          {rows.map((r) => <option key={r.id} value={r.id}>{r.label} · v{r.version}</option>)}
        </select>
        <Link data-testid={`ks-setup-${kind}-new`} to={`/profiles/${kind}/new`}
              className="text-[11px] px-2 py-1.5 rounded border border-[var(--rs-border)] text-[var(--rs-amber)] whitespace-nowrap"
              style={{ minHeight: 36, lineHeight: "30px" }}>+ New</Link>
      </div>
    </label>
  );
}

function StepAutoPop({ session, setSession }) {
  const ed = (key, field, val) => setSession({ ...session, [key]: { ...session[key], [field]: val } });
  return (
    <div data-testid="ks-step-autopop" className="flex flex-col gap-3">
      <h2 className="text-[14px] font-semibold">Review auto-populated data</h2>
      <p className="text-[11px] text-[var(--rs-text-3)]">
        Values pulled from the chosen profiles. Override any cell if your tally
        differs — overrides only affect this kill sheet, not the profile.
      </p>
      <Group title="Section lengths">
        {["dp","hwdp","dc","dpHwdpOpenHole","dcOpenHole","dpInCasing","dcInCasing"].map((f) => (
          <Inline key={f} label={f} testid={`ks-len-${f}`} value={session.lengths[f]}
                  onChange={(v) => ed("lengths", f, v)} />
        ))}
      </Group>
      <Group title="Section capacities">
        {["dpInternal","hwdpInternal","dcInternal","dpHwdpInOpenHole","dcInOpenHole","dpInCasing","dcInCasing"].map((f) => (
          <Inline key={f} label={f} testid={`ks-cap-${f}`} value={session.capacities[f]}
                  step="0.00001"
                  onChange={(v) => ed("capacities", f, v)} />
        ))}
      </Group>
    </div>
  );
}

function StepWellSnapshot({ session }) {
  const w = session.well || {};
  const bhaLen = Array.isArray(session.bha?.components)
    ? session.bha.components.reduce((s, c) => s + (parseFloat(c.length) || 0), 0) : 0;
  const md = parseFloat(w.currentMd) || 0;
  const shoeMd = parseFloat(w.shoeMd) || 0;
  const rows = [
    ["Well Name",     w.label || "—"],
    ["MD",            md.toFixed(0)],
    ["TVD",           (parseFloat(w.currentTvd) || 0).toFixed(0)],
    ["Shoe MD",       shoeMd.toFixed(0)],
    ["Shoe TVD",      (parseFloat(w.shoeTvd) || 0).toFixed(0)],
    ["Hole Size",     w.holeSize || "—"],
    ["Mud Density",   session.mud?.density || "—"],
    ["BHA Length",    bhaLen.toFixed(0)],
    ["DP Length",     Math.max(0, md - bhaLen).toFixed(0)],
    ["Open Hole",     Math.max(0, md - shoeMd).toFixed(0)],
  ];
  return (
    <div data-testid="ks-step-snapshot" className="flex flex-col gap-3">
      <h2 className="text-[14px] font-semibold">Well Snapshot</h2>
      <p className="text-[11px] text-[var(--rs-text-3)]">
        One-glance summary of the configured well before kick data entry.
      </p>
      <div className="grid grid-cols-2 gap-2">
        {rows.map(([k, v]) => (
          <div key={k} data-testid={`ks-snapshot-${k.toLowerCase().replace(/\s+/g, '-')}`}
               className="card-rs p-2 flex justify-between text-[12px]">
            <span className="text-[var(--rs-text-3)]">{k}</span>
            <span className="text-[var(--rs-text-1)] font-mono">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepKickData({ session, setSession }) {
  const v = validateSession(session);
  const set = (field, val) => setSession({ ...session, kickData: { ...session.kickData, [field]: val } });
  return (
    <div data-testid="ks-step-kick" className="flex flex-col gap-3">
      <h2 className="text-[14px] font-semibold">Kick Data</h2>
      <p className="text-[11px] text-[var(--rs-text-3)]">
        Enter shut-in pressures and pit gain. These are the only values you
        type on the kill sheet itself — everything else is computed.
      </p>
      <Inline label="SIDPP"    testid="ks-kick-sidpp"    value={session.kickData.sidpp}   onChange={(v) => set("sidpp", v)} />
      <Inline label="SICP"     testid="ks-kick-sicp"     value={session.kickData.sicp}    onChange={(v) => set("sicp", v)} />
      <Inline label="Pit Gain" testid="ks-kick-pitGain"  value={session.kickData.pitGain} onChange={(v) => set("pitGain", v)} />
      {!v.ok && (
        <div data-testid="ks-kick-errors" className="card-rs p-2 text-[11px]"
             style={{ borderColor: "rgba(252,165,165,0.5)", background: "rgba(252,165,165,0.06)" }}>
          <div className="font-semibold text-[#FCA5A5] uppercase tracking-wider mb-1 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Cannot compute — fix:
          </div>
          <ul className="list-disc list-inside text-[var(--rs-text-1)]">
            {v.errors.map((e, i) => <li key={i}>{e.message}</li>)}
          </ul>
        </div>
      )}
      {v.ok && v.warnings.length > 0 && (
        <div data-testid="ks-kick-warnings" className="card-rs p-2 text-[11px]"
             style={{ borderColor: "rgba(245,180,0,0.4)", background: "rgba(245,180,0,0.05)" }}>
          <div className="font-semibold text-[var(--rs-amber)] uppercase tracking-wider mb-1">Warnings</div>
          <ul className="list-disc list-inside text-[var(--rs-text-2)]">
            {v.warnings.map((w, i) => <li key={i}>{w.message}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function StepReview({ session, composed }) {
  if (!composed || !composed.ok) {
    return <div className="text-[12px] text-[var(--rs-text-3)]">Press “Compute Kill” to proceed.</div>;
  }
  const o = composed.output;
  return (
    <div data-testid="ks-step-review" className="flex flex-col gap-3">
      <h2 className="text-[14px] font-semibold">Computed Outputs</h2>
      <div className="grid grid-cols-2 gap-2">
        <Cell label="KMD"        cell={o.killCalc.kmd}     unit={session.units === "SI" ? "kg/m³" : "ppg"} />
        <Cell label="KMG"        cell={o.killCalc.kmg}     unit={session.units === "SI" ? "kPa/m" : "psi/ft"} />
        <Cell label="ICP"        cell={o.killCalc.icp}     unit={session.units === "SI" ? "kPa" : "psi"} />
        <Cell label="FCP"        cell={o.killCalc.fcp}     unit={session.units === "SI" ? "kPa" : "psi"} />
        <Cell label="K/100 stk"  cell={o.killCalc.kPer100} unit={session.units === "SI" ? "kPa" : "psi"} />
        <Cell label="MAASP_new"  cell={o.additionalOutputs.maaspNew} unit={session.units === "SI" ? "kPa" : "psi"} />
        <Cell label="BHP"        cell={o.additionalOutputs.formationPressure} unit={session.units === "SI" ? "kPa" : "psi"} />
        <Cell label="Frac Margin" cell={o.additionalOutputs.fractureMargin} unit={session.units === "SI" ? "kPa" : "psi"} />
      </div>
      <div className="text-[11px] text-[var(--rs-text-3)] mt-1">
        Step-down: {o.killCalc.stepDown.length} rows · ICP {o.killCalc.icp.display} → FCP {o.killCalc.fcp.display}.
      </div>
    </div>
  );
}

function StepExport({ session, composed, onSaved }) {
  const pro = isProActive(getCachedEntitlement());
  const [saved, setSaved] = useState(false);
  const handleSave = () => {
    if (!pro) return;
    saveKillSheet(session, composed.output);
    setSaved(true);
  };
  return (
    <div data-testid="ks-step-export" className="flex flex-col gap-3">
      <h2 className="text-[14px] font-semibold">Export & Save</h2>
      {!pro ? (
        <div data-testid="ks-export-pro-gate" className="card-rs p-3 text-[12px] flex items-start gap-2"
             style={{ borderColor: "rgba(245,180,0,0.45)", background: "rgba(245,180,0,0.06)" }}>
          <Lock className="w-4 h-4 text-[var(--rs-amber)] mt-0.5" />
          <div>
            <strong className="text-[var(--rs-amber)]">Pro feature.</strong>{" "}
            Saving kill sheets, exporting IWCF-style PDFs, and the historical
            kill-sheet library require RigSentinel Pro.
            <Link to="/pricing" className="ml-1 underline">Upgrade</Link>
          </div>
        </div>
      ) : (
        <>
          <Link
            data-testid="ks-print"
            to={`/kill-sheet/print/${session.id}`}
            target="_blank"
            onClick={() => sessionStorage.setItem(`rsc.killsheet.print.${session.id}`,
                                                 JSON.stringify({ session, output: composed.output }))}
            className="inline-flex items-center gap-2 self-start px-4 py-2 rounded text-[13px] font-medium"
            style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 40 }}
          >
            <Printer className="w-4 h-4" /> Open printable IWCF kill sheet
          </Link>
          <button data-testid="ks-save" onClick={handleSave}
                  className="self-start text-[12px] px-3 py-1.5 rounded border border-[var(--rs-border)] text-[var(--rs-text-2)]"
                  style={{ minHeight: 36 }}>
            {saved ? "Saved ✓" : "Save to Kill Sheet Library"}
          </button>
        </>
      )}
    </div>
  );
}

// -- shared mini inputs -------------------------------------------------------
function Inline({ label, value, onChange, testid, step = "0.001" }) {
  return (
    <label className="text-[11px] grid grid-cols-3 items-center gap-2">
      <span className="text-[var(--rs-text-3)]">{label}</span>
      <input type="number" step={step} data-testid={testid} value={value ?? ""}
             onChange={(e) => onChange(e.target.value)}
             className="input-rs col-span-2" />
    </label>
  );
}
function Group({ title, children }) {
  return (
    <fieldset className="border-t border-[var(--rs-border)] pt-2">
      <legend className="text-[10px] font-mono uppercase tracking-wider text-[var(--rs-text-3)] px-2">{title}</legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{children}</div>
    </fieldset>
  );
}
function Cell({ label, cell, unit }) {
  return (
    <div className="card-rs p-2 flex flex-col"
         data-testid={`ks-cell-${label.toLowerCase().replace(/[^a-z]/g, '')}`}
         title={cell?.formula || ""}>
      <span className="text-[10px] uppercase font-mono text-[var(--rs-text-3)]">{label}</span>
      <span className="text-[14px] font-mono text-[var(--rs-amber)]">
        {cell?.display ?? "—"} <span className="text-[10px] text-[var(--rs-text-3)]">{unit}</span>
      </span>
    </div>
  );
}
